/* eslint-disable react-hooks/exhaustive-deps */
import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { addAppealCouncilLitigation } from "services/advocateComplaintService";
import {
  fetchAppealCouncilDecisionTypes,
  fetchAppealDecisionMeasureTypes,
} from "services/prosecutorDecipilineService";
import { AdvocateComplaints, CouncilLitigation } from "types";
import { LoadingIcon } from "utils/constant";
import { useTranslation } from "contexts/i18nContext";
import "../../complaintRequest/styles/AddEvidenceModal.css";

interface DecisionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: AdvocateComplaints;
  councilLitigationData: CouncilLitigation;
  onRefresh: any;
}

export default function AddAppealCouncilLitigationDecisionModal({
  isOpen,
  setIsOpen,
  councilLitigationData,
  complaintData,
  onRefresh,
}: DecisionModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  const [formState, setFormState] = useState({
    files: [] as File[],
    council_decision_summary: "",
    council_decision_day: "",
    council_decision_type: "",
    discipline_measure_type: "",
  });
  const [decisionTypes, setDecisionTypes] = useState<any>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadDecisionTypes = useCallback(async () => {
    try {
      const resData = await fetchAppealCouncilDecisionTypes();
      if (resData) {
        setDecisionTypes(resData);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDecisionMeasureTypes = useCallback(async () => {
    try {
      await fetchAppealDecisionMeasureTypes();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDecisionTypes();
    loadDecisionMeasureTypes();
  }, []);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...prev.files, ...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!councilLitigationData) return;

    setLoading(true);

    const data = new FormData();
    data.append("council_decision", formState?.files[0] || "");
    data.append("council_decision_summary", formState.council_decision_summary);
    data.append("council_decision_day", formState.council_decision_day);
    data.append(
      "ammended_council_decision_type",
      formState.council_decision_type,
    );
    data.append(
      "ammended_discipline_measure_type",
      formState.discipline_measure_type,
    );

    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("accused_advocate_name", complaintData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      complaintData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      complaintData?.accused_license_number || "",
    );

    data.append(
      "is_final_verdict_given",
      String(councilLitigationData?.is_final_verdict_given),
    );

    councilLitigationData?.summon &&
      data.append("summon", String(councilLitigationData?.summon));

    councilLitigationData?.summon_appeal_accused_advocate &&
      data.append(
        "summon_appeal_accused_advocate",
        String(councilLitigationData?.summon_appeal_accused_advocate),
      );

    councilLitigationData?.appealto_court &&
      data.append(
        "appealto_court",
        String(councilLitigationData?.appealto_court),
      );

    try {
      await addAppealCouncilLitigation(data, true);

      onRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");

      setFormState({
        files: [] as File[],
        council_decision_summary: "",
        council_decision_day: "",
        council_decision_type: "",
        discipline_measure_type: "",
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("addCouncilDecision")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("councilDecisionDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type="date"
              name="council_decision_day"
              placeholder={t("egJune122014")}
              value={formState.council_decision_day}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("councilDecisionType")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="council_decision_type"
              value={formState.council_decision_type}
              onChange={handleChange}
            >
              <option value="">{t("selectDisciplineType")}</option>
              {decisionTypes?.map((list: any) => (
                <option key={list.id} value={list.decision_type}>
                  {list.decision_type}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("councilDecisionSummary")}
            </label>
            <textarea
              required
              rows={3}
              name="council_decision_summary"
              value={formState.council_decision_summary}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("councilDecisionFile")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>

            {formState.files.length > 0 && (
              <div className="file-list">
                {formState.files.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("addDecision")
          )}
        </button>
      </form>
    </SideModal>
  );
}
